import {
  downloadDriveFile,
  DriveFile,
  listFolder,
  listFolderRecursive,
} from 'helpers/google-drive';
import {
  FileFetcher,
  ImportPlan,
  ImportPlanAlbum as GenericPlanAlbum,
} from 'helpers/import-types';

export type ImportPlanAlbum = GenericPlanAlbum<DriveFile, DriveFile>;
export type { ImportProgress, ImportRunResult } from 'helpers/import-types';

export type DrivePlan = ImportPlan<DriveFile, DriveFile>;

/**
 * Scan a Drive folder one level deep: each immediate subfolder becomes an
 * album whose memories are all importable files under it (recursive), and
 * importable files directly in the folder come back as unsorted.
 */
export const planDriveImport = async (
  rootFolder: DriveFile,
  isCancelled?: () => boolean,
): Promise<DrivePlan> => {
  const subfolders: DriveFile[] = [];
  const unsorted: DriveFile[] = [];
  let pageToken: string | undefined;
  do {
    // eslint-disable-next-line no-await-in-loop
    const page = await listFolder(rootFolder.id, pageToken);
    page.files.forEach((f) => {
      if (f.isFolder) subfolders.push(f);
      else if (f.isImportable) unsorted.push(f);
    });
    pageToken = page.nextPageToken;
  } while (pageToken && !isCancelled?.());

  const planned: ImportPlanAlbum[] = [];
  for (let i = 0; i < subfolders.length; i += 1) {
    if (isCancelled?.()) break;
    const folder = subfolders[i];
    // eslint-disable-next-line no-await-in-loop
    const files = await listFolderRecursive(folder.id);
    if (files.length > 0) {
      planned.push({ folder, files });
    }
  }

  return { rootName: rootFolder.name, albums: planned, unsorted };
};

// The Rails presigned-url endpoint reads byte_size, content_type and filename
// off the File, so wrap the downloaded Blob in one.
export const fetchDriveFile: FileFetcher<DriveFile> = async (driveFile) => {
  const downloaded = await downloadDriveFile(driveFile);
  const lastModified = downloaded.modifiedTime
    ? Date.parse(downloaded.modifiedTime)
    : Date.now();
  return new File([downloaded.blob], downloaded.name, {
    type: downloaded.mimeType || 'application/octet-stream',
    lastModified: Number.isFinite(lastModified) ? lastModified : Date.now(),
  });
};
